/* eslint-disable react/prop-types */
import { useState, useEffect, memo, useCallback } from 'react';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Dialog, { dialogClasses } from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { useBoolean } from 'src/hooks/use-boolean';
import { useEventListener } from 'src/hooks/use-event-listener';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useRouter } from 'src/routes/hooks';
import SearchNotFound from 'src/components/search-not-found';
import ResultItem from './result-item';
import { useNavData } from '../../dashboard/config-navigation';
import { applyFilter, groupedData, getAllItems } from './utils';

// ----------------------------------------------------------------------

function SearchTrigger({ onClick, isDark }) {
  const TEAL = '#1D9E75';
  const TEAL_LIGHT = '#5DCAA5';

  const [isMac, setIsMac] = useState(true);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isMacUser = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      setIsMac(isMacUser);
    }
  }, []);

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label="Search screens (⌘K)"
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',

        // 1. Fluid, dynamic widths for every screen size
        width: { xs: 32, sm: 200, md: 260, lg: 320 },
        height: 32,
        borderRadius: '8px',
        ml: { xs: 1, md: 3 },
        border: '1.5px solid',
        borderColor: isDark ? alpha(TEAL, 0.35) : alpha(TEAL, 0.5),
        bgcolor: isDark ? alpha(TEAL, 0.07) : alpha(TEAL, 0.04),
        cursor: 'pointer',

        // 2. Added 'width' transition so resizing feels smooth
        transition: 'width 0.2s, border-color 0.15s, box-shadow 0.15s, background-color 0.15s',

        '&:hover': {
          borderColor: isDark ? alpha(TEAL_LIGHT, 0.6) : TEAL,
          bgcolor: isDark ? alpha(TEAL, 0.12) : alpha(TEAL, 0.07),
          boxShadow: `0 0 0 3px ${alpha(TEAL, 0.1)}`,
        },
        '&:focus-visible': {
          outline: 'none',
          boxShadow: `0 0 0 3px ${alpha(TEAL, 0.2)}`,
          borderColor: TEAL,
        },
      }}
    >
      {/* Search icon */}
      <Box
        sx={{
          position: 'absolute',
          // 3. Dynamically center the icon on mobile, align left on desktop
          left: { xs: '50%', sm: 8 },
          transform: { xs: 'translateX(-50%)', sm: 'none' },
          display: 'flex',
          alignItems: 'center',
          color: isDark ? TEAL_LIGHT : TEAL,
          flexShrink: 0,
          transition: 'all 0.2s',
        }}
      >
        <Iconify icon="eva:search-fill" width={16} />
      </Box>

      {/* Placeholder text */}
      <Typography
        sx={{
          pl: '30px',
          pr: '60px',
          fontSize: 12,
          color: isDark ? 'rgba(255,255,255,0.3)' : 'text.disabled',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          // 4. Handle visibility natively via CSS instead of JS rendering
          display: { xs: 'none', sm: 'block' },
        }}
      >
        Search screens…
      </Typography>

      {/* Dynamic OS Shortcut Badge */}
      <Box
        sx={{
          position: 'absolute',
          right: 8,
          // 4. Hide badge on mobile (phones don't have keyboards anyway)
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
          border: '0.5px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
          borderRadius: '4px',
          px: 0.75,
          py: 0.15,
        }}
      >
        <Typography
          component="kbd"
          sx={{
            fontSize: 10,
            fontFamily: 'monospace',
            color: isDark ? 'rgba(255,255,255,0.35)' : 'text.disabled',
            letterSpacing: '0.02em',
            lineHeight: 1.6,
            fontWeight: 600,
          }}
        >
          {isMac ? '⌘ + K' : 'Ctrl + K'}
        </Typography>
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------------

function Searchbar() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const router = useRouter();
  const search = useBoolean();

  const [searchQuery, setSearchQuery] = useState('');
  const navData = useNavData();

  const handleClose = useCallback(() => {
    search.onFalse();
    setSearchQuery('');
  }, [search]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        search.onToggle();
        setSearchQuery('');
      }
    },
    [search]
  );

  useEventListener('keydown', handleKeyDown);

  const handleClick = useCallback(
    (path) => {
      if (path.includes('http')) {
        window.open(path);
      } else {
        router.push(path);
      }
      handleClose();
    },
    [handleClose, router]
  );

  const handleSearch = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  const dataFiltered = applyFilter({
    inputData: getAllItems({ data: navData }),
    query: searchQuery,
  });

  const notFound = searchQuery && !dataFiltered.length;

  const renderItems = () => {
    const data = groupedData(dataFiltered);

    return Object.keys(data)
      .sort((a, b) => -b.localeCompare(a))
      .map((group, index) => (
        <List key={group || index} disablePadding>
          {data[group].map((item) => {
            const { title, path } = item;
            const partsTitle = parse(title, match(title, searchQuery));
            const partsPath = parse(path, match(path, searchQuery));

            return (
              <ResultItem
                path={partsPath}
                title={partsTitle}
                key={`${title}${path}`}
                groupLabel={searchQuery && group}
                onClickItem={() => handleClick(path)}
              />
            );
          })}
        </List>
      ));
  };

  return (
    <>
      {/* 5. Removed mdUp prop here as the component is now fully CSS responsive */}
      <SearchTrigger onClick={search.onTrue} isDark={isDark} />

      <Dialog
        fullWidth
        maxWidth="sm"
        open={search.value}
        onClose={handleClose}
        transitionDuration={{
          enter: theme.transitions.duration.shortest,
          exit: 0,
        }}
        PaperProps={{
          sx: {
            mt: { xs: 2, sm: 5 },
            overflow: 'hidden',
            width: '100%',
            borderRadius: 1,
            border: '0.5px solid',
            borderColor: 'divider',
            ...(isDark && {
              bgcolor: 'grey.900',
            }),
            maxWidth: { sm: '680px' },
          },
        }}
        sx={{
          [`& .${dialogClasses.container}`]: {
            alignItems: 'flex-start',
          },
        }}
      >
        {/* Search input row */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: `solid 1px ${theme.palette.divider}`,
          }}
        >
          <Iconify
            icon="eva:search-fill"
            width={22}
            sx={{ color: 'text.disabled', flexShrink: 0 }}
          />

          <InputBase
            fullWidth
            autoFocus
            placeholder="Search screens…"
            value={searchQuery}
            onChange={handleSearch}
            inputProps={{
              sx: { typography: 'body1', py: 0 },
            }}
            sx={{ flex: 1 }}
          />

          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              flexShrink: 0,
              width: 28,
              height: 28,
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* Results */}
        <Scrollbar
          sx={{
            p: 2,
            pt: 1.5,
            maxHeight: {
              xs: 'min(780px, calc(100vh - 120px))', 
              sm: 'min(780px, calc(100vh - 152px))',
              md: 'min(780px, calc(100vh - 152px))',
              lg: 'min(780px, calc(100vh - 152px))',
            },
          }}
        >
          {notFound ? <SearchNotFound query={searchQuery} sx={{ py: 10 }} /> : renderItems()}
        </Scrollbar>
      </Dialog>
    </>
  );
}

export default memo(Searchbar);
